import {useReducer} from 'react'

/**
 * This function computes the bounds for the image based on the new scale, old scale, and the container dimensions.
 *
 * @private
 * @param newScale
 * @param oldScale
 * @param zoomableElementRef
 * @param containerRef
 * @returns {{left: number, top: number, right: number, bottom: number}}
 */
const computeBounds = ({newScale, oldScale, zoomableElementRef, containerRef}) => {
    if (!zoomableElementRef.current || !containerRef) {
        return {left: 0, top: 0, right: 0, bottom: 0}
    }
    const containerRect = containerRef.current.getBoundingClientRect()
    const imageRect = zoomableElementRef.current.getBoundingClientRect()
    const maxX = Math.max(
        0,
        ((imageRect.width * newScale) / oldScale - containerRect.width) / 2 / newScale
    )
    const maxY = Math.max(
        0,
        ((imageRect.height * newScale) / oldScale - containerRect.height) / 2 / newScale
    )
    return {
        left: -maxX,
        top: -maxY,
        right: maxX,
        bottom: maxY
    }
}

const initialState = {scale: 1, pan: {x: 0, y: 0}, maxScale: 2}
/**
 * This is a helper hook for managing the transformation of a zoomable element inside another element with the use of useGesture.
 *
 * @param {{current: HTMLElement}} containerRef - the container element that holds the zoomable element
 * @param {{current: HTMLElement}} zoomableElementRef - the element that will be zoomed and panned
 * @param {number} maxScale - the maximum scale factor for zooming
 * @param {boolean} enabled - whether the zoom gestures are enabled or not
 * @returns {{
 *   state: {transform: string, isZoomed: boolean},
 *   gestureHandlers: {onDrag: function, onPinch: function},
 *   gestureConfig: {
 *     target: *,
 *     eventOptions: {capture: boolean, passive: boolean},
 *     drag: Object,
 *     wheel: Object,
 *     pinch: Object,
 *     scroll: Object,
 *     hover: Object
 *   }
 * }}
 */
const useZoomGestures = ({containerRef, zoomableElementRef, maxScale, enabled = true}) => {
    function reducer(state, action) {
        const postProcess = (_newState) => {
            const {
                scale: newScale = state.scale,
                pan: {x: newPanX, y: newPanY} = state.pan,
                ...newState
            } = _newState
            const clampedScale = Math.max(1, Math.min(maxScale, newScale))
            const newBounds = computeBounds({
                newScale: clampedScale,
                oldScale: state.scale,
                zoomableElementRef,
                containerRef
            })
            const clampedPanX = Math.max(newBounds.left, Math.min(newBounds.right, newPanX))
            const clampedPanY = Math.max(newBounds.top, Math.min(newBounds.bottom, newPanY))
            return {
                ...state,
                ...newState,
                scale: clampedScale,
                pan: {
                    x: clampedPanX,
                    y: clampedPanY
                },
                bounds: newBounds
            }
        }

        switch (action.type) {
            case 'toggleScale':
                return postProcess({
                    scale: state.scale === 1 ? maxScale : 1,
                    pan: state.scale === 1 ? action.pan ?? {x: 0, y: 0} : {x: 0, y: 0}
                })
            case 'setScale':
                return postProcess({
                    scale: action.scale
                })
            case 'setPan':
                return postProcess({pan: action.pan})
            case 'pinch':
                return postProcess({
                    ...(action.event.first && {
                        startPinchCenter: action.center,
                        startPinchPan: state.pan,
                        startPinchScale: state.scale,
                        scale: state.scale * action.scale
                    }),
                    ...(!action.event.first && {
                        scale: state.startPinchScale * action.scale,
                        pan: {
                            x:
                                state.startPinchPan.x -
                                (state.startPinchCenter.x - action.center.x) /
                                    Math.max(
                                        1,
                                        Math.min(maxScale, state.startPinchScale * action.scale)
                                    ),
                            y:
                                state.startPinchPan.y -
                                (state.startPinchCenter.y - action.center.y) /
                                    Math.max(
                                        1,
                                        Math.min(maxScale, state.startPinchScale * action.scale)
                                    )
                        }
                    }),
                    ...(action.event.last && {
                        startPinchCenter: null,
                        startPinchPan: null,
                        startPinchScale: null
                    })
                })
            case 'reset':
                return initialState
            default:
                return state
        }
    }

    const [state, dispatch] = useReducer(reducer, initialState)

    return {
        state: {
            transform: `scale(${state.scale}) translateX(${state.pan.x}px) translateY(${state.pan.y}px)`,
            isZoomed: state.scale > 1
        },
        gestureHandlers: {
            onDrag: (e) => {
                const {
                    // the coordinates of the event relative to the container
                    xy: [x, y],
                    // the offset of the drag relative to the initial position - for tap events it is always [0, 0]
                    offset: [ox, oy],
                    tap
                } = e

                if (tap) {
                    const rect = zoomableElementRef.current.getBoundingClientRect()
                    const centerX = rect.left + rect.width / 2
                    const centerY = rect.top + rect.height / 2
                    const offsetX = x - centerX
                    const offsetY = y - centerY
                    const newPanX = (-offsetX * (maxScale - 1)) / maxScale
                    const newPanY = (-offsetY * (maxScale - 1)) / maxScale
                    dispatch({
                        type: 'toggleScale',
                        pan: {
                            x: newPanX,
                            y: newPanY
                        }
                    })
                } else {
                    dispatch({type: 'setPan', pan: {x: ox, y: oy}})
                }
            },
            onPinch: (e) => {
                const {
                    origin: [originX, originY],
                    movement: [scale]
                } = e
                dispatch({type: 'pinch', scale, center: {x: originX, y: originY}, event: e})
            }
        },
        gestureConfig: {
            target: enabled && zoomableElementRef,
            eventOptions: {capture: true, passive: false},
            drag: {
                filterTaps: true,
                preventDefault: true,
                // set the bounds for dragging so the image does not go out of bounds
                bounds: state.bounds,
                transform: ([x, y]) => [x / state.scale, y / state.scale]
            },
            wheel: {enabled: false},
            pinch: {},
            scroll: {enabled: false},
            hover: {enabled: false}
        }
    }
}

export default useZoomGestures
